import React, { useState, type ReactNode } from 'react';
import {
  ActivityIndicator,
  Pressable,
  Platform,
  useWindowDimensions,
  ScrollView,
  StyleSheet,
  Switch,
  TextInput,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import type { DataState, IconName, Tone } from '../domain/types';
import { useTokens } from '../theme/tokens';
import { Icon } from './Icon';
import { Text, useCopy } from '../copy';
import {ButtonRow,useButtonColumns} from '../../ButtonLayout';
import {useScrollPosition} from './ScrollPosition';
import {useSaveFeedback} from './SaveFeedback';
import {useDesktop} from './useDesktop';
import {useLayout} from '../../LayoutContext';
import {buttonVisuals} from '../../../shared/button-visuals.js';
import {scaleTypography} from '../../../shared/appearance-model.js';
import {usePageFrame} from '../../usePageFrame';

const toneColor = (tone: Tone, colors: ReturnType<typeof useTokens>["colors"]) => ({
  neutral: colors.textMuted,
  positive: colors.positive,
  attention: colors.attention,
  critical: colors.critical,
  info: colors.info,
}[tone]);

export function Screen({ children, testID }: { children: ReactNode; testID?: string }) {
  const frame=usePageFrame();
  const {desktop}=useDesktop();
  const scrollPosition=useScrollPosition();
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  const small=useWindowDimensions().width<420;
  return <ScrollView {...scrollPosition} onLayout={e=>{frame.onLayout(e);scrollPosition.onLayout(e);}} testID={testID} keyboardShouldPersistTaps="handled" style={[styles.screen,frame.scrollStyle]} contentContainerStyle={[styles.screenContent,frame.contentStyle]}>{children}</ScrollView>;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  return <Text style={styles.eyebrow}>{children}</Text>;
}

export function Title({ children, subtitle, icon, onBack, backDisabled=false }: { children: ReactNode; subtitle?: string; icon?:ReactNode; onBack?:()=>void; backDisabled?:boolean }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  return (
    <View style={styles.titleWrap}>
      <View style={{flexDirection:"row",alignItems:"center",gap:12}}>{onBack && <Pressable accessibilityRole="button" accessibilityLabel={copy("Tilbage")} accessibilityState={{disabled:backDisabled}} disabled={backDisabled} onPress={onBack} style={({pressed})=>({width:48,height:48,borderRadius:radius.button,backgroundColor:colors.surfaceSoft,alignItems:"center",justifyContent:"center",opacity:backDisabled?.4:pressed?.7:1})}><Icon name="back" size={26} color={colors.text}/></Pressable>}{icon}<Text accessibilityRole="header" style={[styles.title,{flex:1,minWidth:0,flexShrink:1}]}>{children}</Text></View>
      {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
    </View>
  );
}

export function SectionTitle({ children, action, onAction }: { children: ReactNode; action?: string; onAction?: () => void }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  return (
    <View style={styles.sectionTitleRow}>
      <Text accessibilityRole="header" style={styles.sectionTitle}>{children}</Text>
      {action && onAction ? <Button label={action} onPress={onAction} tone="secondary" compact /> : null}
    </View>
  );
}

export function Card({ children, style, tone = 'neutral' }: { children: ReactNode; style?: StyleProp<ViewStyle>; tone?: Tone }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  return <View style={[styles.card, tone !== 'neutral' && { borderColor: toneColor(tone, colors) }, style]}>{children}</View>;
}

export function Pill({ children, tone = 'neutral', icon, prominent=false }: { children: ReactNode; tone?: Tone; icon?: IconName; prominent?:boolean }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  const color = toneColor(tone, colors);
  return (
    <View style={[styles.pill, { borderColor: color },prominent&&{minHeight:34,paddingHorizontal:12,paddingVertical:5,gap:7}]}>
      {icon ? <Icon name={icon} size={prominent?22:design.accessible ? 20 : 16} color={color} /> : null}
      <Text style={[styles.pillText, { color },prominent&&{fontSize:type.label+2}]}>{children}</Text>
    </View>
  );
}

export function Button({ label, onPress, icon, tone = 'primary', disabled = false, compact=false, equalWidth=false, fontSize,singleLine=false,minHeight }: { label: string; onPress: () => unknown; icon?: IconName; tone?: 'primary' | 'secondary' | 'danger' | 'quiet' | 'brand'; disabled?: boolean; compact?:boolean; equalWidth?:boolean; fontSize?:number;singleLine?:boolean;minHeight?:number }) {
  const columns=useButtonColumns(),[focused,setFocused]=useState(false);
  const saveFeedback=useSaveFeedback();
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  const resolvedIcon=icon==='save'?'confirm':icon;
  const saveAction=resolvedIcon==='confirm'&&/^(Gem|Save)(?:\s|$)/i.test(label);
  const press=()=>{const result=onPress();if(saveAction&&result&&typeof (result as Promise<unknown>).then==='function')saveFeedback.track(result as Promise<unknown>);};
  const visual=buttonVisuals(colors,tone),backgroundColor=visual.backgroundColor,foreground=visual.color;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={press}
      onFocus={()=>setFocused(true)}
      onBlur={()=>setFocused(false)}
      style={({ pressed }) => [styles.button, minHeight!=null&&{minHeight}, compact && {minHeight:48,paddingHorizontal:12,paddingVertical:10,gap:8}, columns>0 && {flexGrow:1,minWidth:0,paddingHorizontal:10,gap:8}, equalWidth && {flex:1,flexBasis:0,minWidth:0,paddingHorizontal:10,gap:8}, singleLine&&{justifyContent:'flex-start',paddingHorizontal:8,gap:6}, { backgroundColor,borderWidth:1,borderColor:visual.borderColor }, focused&&{borderWidth:2,borderColor:colors.text,...(Platform.OS==='web'?{outlineStyle:'solid',outlineColor:colors.primary,outlineWidth:2,outlineOffset:3} as ViewStyle:{})}, pressed && styles.pressed, disabled && styles.disabled]}
    >
      {resolvedIcon ? <Icon name={resolvedIcon} size={22} color={foreground} disabled={disabled} /> : null}
      <Text numberOfLines={undefined} adjustsFontSizeToFit={false} minimumFontScale={0.85} style={[styles.buttonText, equalWidth && {minWidth:0}, singleLine&&{textAlign:'left',minWidth:0}, { color: foreground },fontSize?{fontSize}:undefined]}>{label}</Text>
    </Pressable>
  );
}

export function IconButton({ label, name, onPress, selected = false }: { label: string; name: IconName; onPress: () => void; selected?: boolean }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  return (
    <Pressable accessibilityRole="button" accessibilityLabel={copy(label)} aria-pressed={selected} accessibilityState={{ selected }} onPress={onPress} style={({ pressed }) => [styles.iconButton, selected && styles.iconButtonSelected, pressed && styles.pressed]}>
      <Icon name={name} size={design.accessible ? 28 : 22} color={selected ? colors.primaryInk : colors.text} />
      {design.accessible ? <Text style={[styles.iconButtonLabel, selected && { color: colors.primaryInk }]}>{label}</Text> : null}
    </Pressable>
  );
}

export function Metric({ label, value, note, icon }: { label: string; value: string; note?: string; icon?: IconName }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  return (
    <View style={styles.metric} accessibilityLabel={`${copy(label)}: ${copy(value)}${note ? `. ${copy(note)}` : ''}`}>
      <View style={styles.metricLabelRow}>{icon ? <Icon name={icon} size={24} color={colors.textMuted} /> : null}<Text style={styles.metricLabel}>{label}</Text></View>
      <Text style={styles.metricValue}>{value}</Text>
      {note ? <Text style={styles.metricNote}>{note}</Text> : null}
    </View>
  );
}

// The shared modern row wraps only when its container becomes too narrow.
// Theme and text size never select a different grid or hide readings.
export function MetricRow({children}:{children:ReactNode}) {
  const {spacing}=useTokens();
  return <View style={{flexDirection:'row',flexWrap:'wrap',gap:spacing.md}}>{children}</View>;
}

export function Row({ title, value, icon, tone = 'neutral', onPress, subtitle, leading }: { title: string; value?: string; icon?: IconName; leading?:ReactNode; tone?: Tone; onPress?: () => void; subtitle?: string }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  const content = (
    <>
      {leading ? <View style={styles.rowIcon}>{leading}</View> : icon ? <View style={styles.rowIcon}><Icon name={icon} size={design.accessible ? 28 : 22} color={tone === 'neutral' ? colors.primary : toneColor(tone, colors)} /></View> : null}
      <View style={styles.rowCopy}>
        <Text style={styles.rowTitle}>{title}</Text>
        {subtitle ? <Text style={styles.rowSubtitle}>{subtitle}</Text> : null}
      </View>
      {value ? <Text style={[styles.rowValue, tone !== 'neutral' && { color: toneColor(tone, colors) }]}>{value}</Text> : null}
      {onPress ? <Icon name="chevron" size={20} color={colors.textMuted} /> : null}
    </>
  );
  return onPress ? <Pressable accessibilityRole="button" onPress={onPress} style={({ pressed }) => [styles.row, pressed && styles.pressed]}>{content}</Pressable> : <View style={styles.row}>{content}</View>;
}

export function SwitchRow({ title, subtitle, value, onValueChange, disabled = false }: { title: string; subtitle?: string; value: boolean; onValueChange: (value: boolean) => void; disabled?: boolean }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  return (
    <View style={[styles.switchRow, disabled && styles.disabled]}>
      <View style={styles.rowCopy}><Text style={styles.rowTitle}>{title}</Text>{subtitle ? <Text style={styles.rowSubtitle}>{subtitle}</Text> : null}</View>
      <Switch accessibilityLabel={copy(title)} value={value} disabled={disabled} onValueChange={onValueChange} trackColor={{ false: colors.border, true: colors.primary }} thumbColor={value ? colors.primaryInk : colors.textMuted} />
    </View>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label, disabled = false, columns }: { disabled?: boolean; columns?:2|3; value: T; options: ReadonlyArray<{ value: T; label: string; icon?:IconName }>; onChange: (value: T) => void; label: string }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  return (
    <View accessibilityRole="radiogroup" accessibilityLabel={copy(label)} style={[styles.segmented,columns&&{flexWrap:'wrap'}]}>
      {options.map((option) => {
        const selected = value === option.value;
        return <Pressable key={option.value} accessibilityRole="radio" aria-checked={selected} accessibilityState={{ checked: selected, disabled }} disabled={disabled} onPress={() => onChange(option.value)} style={[styles.segment, {flex:undefined,flexGrow:1,flexDirection:'row',alignItems:'center',justifyContent:'center',gap:8},columns&&{flexBasis:columns===3?'30%':'45%'}, selected && styles.segmentSelected,disabled&&styles.disabled]}>{option.icon&&<Icon name={option.icon} size={22} color={selected?colors.primaryInk:colors.primary}/>}<Text style={[styles.segmentText,{flexShrink:1}, selected && styles.segmentTextSelected]}>{option.label}</Text></Pressable>;
      })}
    </View>
  );
}

export function Field({ label, value, onChangeText, keyboardType = 'default', help }: { label: string; value: string; onChangeText: (value: string) => void; keyboardType?: 'default' | 'numeric' | 'phone-pad'; help?: string }) {
  const copy = useCopy();
  const {appearance:{textSize}}=useLayout();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  return (
    <View style={styles.fieldWrap}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput accessibilityLabel={copy(label)} value={value} onChangeText={onChangeText} keyboardType={keyboardType} style={scaleTypography(styles.field,textSize)} placeholderTextColor={colors.textMuted} />
      {help ? <Text style={styles.fieldHelp}>{help}</Text> : null}
    </View>
  );
}

export function StatePanel({ state, title, message, actionLabel, onAction }: { state: DataState; title?: string; message?: string; actionLabel?: string; onAction?: () => void }) {
  const copy = useCopy();
  const tokens = useTokens(), {colors,design,radius,shadows,spacing,touch,type} = tokens;
  const styles = makeStyles(tokens);
  const defaults: Record<DataState, { title: string; message: string; icon: IconName; tone: Tone }> = {
    ready: { title: 'Klar', message: 'Data er tilgængelige.', icon: 'check', tone: 'positive' },
    loading: { title: 'Henter oplysninger', message: 'Vent et øjeblik.', icon: 'refresh', tone: 'info' },
    empty: { title: 'Ingen registreringer', message: 'Der er ingen data i den valgte periode.', icon: 'info', tone: 'neutral' },
    offline: { title: 'Forbindelsen er afbrudt', message: 'Seneste kendte oplysninger bevares med tidspunkt.', icon: 'connection', tone: 'attention' },
    error: { title: 'Noget gik galt', message: "Prøv igen om et øjeblik.", icon: 'warning', tone: 'critical' },
    unsupported: { title: "Ikke tilgængelig endnu", message: "Funktionen er ikke tilgængelig på denne enhed endnu.", icon: 'info', tone: 'attention' },
  };
  const content = defaults[state];
  return (
    <Card tone={content.tone}>
      <View style={styles.stateRow}>
        {state === 'loading' ? <ActivityIndicator color={colors.info} /> : <Icon name={content.icon} size={design.accessible ? 32 : 24} color={toneColor(content.tone, colors)} />}
        <View style={styles.rowCopy}><Text style={styles.stateTitle}>{title ?? content.title}</Text><Text style={styles.stateMessage}>{message ?? content.message}</Text></View>
      </View>
      {actionLabel && onAction ? <Button label={actionLabel} onPress={onAction} tone="secondary" /> : null}
    </Card>
  );
}

const makeStyles = ({colors,design,radius,shadows,spacing,touch,type}: ReturnType<typeof useTokens>) => StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.background },
  screenContent: { paddingHorizontal: spacing.md, paddingTop: spacing.md, paddingBottom: 100, gap: spacing.md },
  eyebrow: { color: colors.primary, fontSize: type.label, fontWeight: '800', textTransform: 'uppercase', letterSpacing: design.accessible ? 0.4 : 1.2 },
  titleWrap: { gap: spacing.xs },
  title: { color: colors.text, fontSize: type.title, lineHeight: type.title + 6, fontWeight: '800' },
  subtitle: { color: colors.textMuted, fontSize: type.body, lineHeight: type.line },
  sectionTitleRow: { minHeight: touch.min, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: spacing.sm },
  sectionTitle: { color: colors.text, fontSize: type.heading, fontWeight: '800', flexGrow: 1, flexBasis: 140, minWidth: 0 },
  textButton: { alignItems: 'center', borderRadius: radius.button, maxWidth: '100%', flexShrink: 1, minHeight: touch.min, justifyContent: 'center', paddingHorizontal: spacing.sm },
  textButtonLabel: { textAlign: 'center', color: colors.primary, fontSize: type.body, fontWeight: '800' },
  card: { backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.border, padding: spacing.md, gap: spacing.md, ...shadows.card },
  pill: { maxWidth: '100%', flexShrink: 1, alignSelf: 'flex-start', minHeight: design.accessible ? 38 : 28, paddingHorizontal: spacing.sm, borderRadius: radius.pill, borderWidth: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  pillText: { flexShrink: 1, fontSize: type.label, fontWeight: '800' },
  button: { alignSelf:'stretch', minWidth:0, minHeight: design.accessible ? 56 : touch.min, borderRadius: radius.button, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs },
  buttonText: { flexShrink: 1, fontSize: type.body, fontWeight: '800', textAlign: 'center' },
  pressed: { opacity: 0.72, transform: [{ scale: 0.99 }] },
  disabled: { opacity: 0.45 },
  iconButton: { minWidth: touch.min, minHeight: touch.min, padding: spacing.sm, borderRadius: radius.button, backgroundColor: colors.surfaceSoft, alignItems: 'center', justifyContent: 'center', gap: spacing.xs },
  iconButtonSelected: { backgroundColor: colors.primary },
  iconButtonLabel: { color: colors.text, fontSize: type.label, fontWeight: '800', textAlign: 'center' },
  metric: { minWidth: design.accessible ? '47%' : 132, flex: 1, gap: spacing.xs },
  metricLabelRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  metricLabel: { flexShrink: 1, color: colors.textMuted, fontSize: type.bodyLarge, lineHeight: type.bodyLarge + 7, fontWeight: '700' },
  metricValue: { color: colors.text, fontSize: type.metric, fontWeight: '800', fontVariant: ['tabular-nums'] },
  metricNote: { color: colors.textMuted, fontSize: type.label, lineHeight: type.label + 5 },
  row: { borderRadius: radius.button, minHeight: design.accessible ? 64 : 52, flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
  rowIcon: { width: design.accessible ? 42 : 32, alignItems: 'center' },
  rowCopy: { flex: 1, minWidth: 0, gap: spacing.xxs },
  rowTitle: { color: colors.text, fontSize: type.bodyLarge, lineHeight: type.bodyLarge + 5, fontWeight: design.accessible ? '800' : '700' },
  rowSubtitle: { color: colors.textMuted, fontSize: type.body, lineHeight: type.line },
  rowValue: { color: colors.textMuted, maxWidth: '42%', textAlign: 'right', fontSize: type.body, lineHeight: type.line },
  switchRow: { minHeight: design.accessible ? 72 : 56, flexDirection: 'row', alignItems: 'center', gap: spacing.md, paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
  segmented: { flexDirection: 'row', flexWrap: 'nowrap', gap: 6 },
  segment: { minHeight: 48, minWidth: 0, flex: 1, alignItems: 'center', justifyContent: 'center', borderRadius: radius.button, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surfaceSoft, paddingHorizontal: 4, paddingVertical: 8 },
  segmentSelected: { backgroundColor: colors.primary, borderColor: colors.primary },
  segmentText: { color: colors.textMuted, fontSize: type.body, fontWeight: '800', textAlign: 'center' },
  segmentTextSelected: { color: colors.primaryInk },
  fieldWrap: { gap: spacing.xs },
  fieldLabel: { color: colors.text, fontSize: type.body, fontWeight: '800' },
  field: { minHeight: design.accessible ? 58 : 48, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surfaceSoft, color: colors.text, fontSize: type.bodyLarge, paddingHorizontal: spacing.md },
  fieldHelp: { color: colors.textMuted, fontSize: type.label, lineHeight: type.label + 5 },
  stateRow: { flexDirection: 'row', gap: spacing.md, alignItems: 'flex-start' },
  stateTitle: { color: colors.text, fontSize: type.bodyLarge, fontWeight: '800' },
  stateMessage: { color: colors.textMuted, fontSize: type.body, lineHeight: type.line },
});

export const useStyles = () => makeStyles(useTokens());
